import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import { useCreateSenderProfile, useCreateBankAccount } from '../hooks/useSettings'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { PhoneInput } from '../components/ui/phone-input'
import { Label } from '../components/ui/label'
import { Button } from '../components/ui/button'
import { Progress } from '../components/ui/progress'
import { CountrySelect } from '../components/ui/country-select'
import { toast } from '../hooks/useToast'
import {
  User,
  Building2,
  CreditCard,
  Check,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Loader2,
  FileText,
  Users,
  BarChart3,
} from 'lucide-react'

const TOTAL_STEPS = 3

export default function Onboarding() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const createSenderProfile = useCreateSenderProfile()
  const createBankAccount = useCreateBankAccount()

  const [currentStep, setCurrentStep] = useState(1)
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward')
  const [isAnimating, setIsAnimating] = useState(false)

  // Step 1: Personal/Business Info
  const [profileData, setProfileData] = useState({
    companyName: '',
    taxId: '',
    email: user?.email || '',
    phone: '',
    street: '',
    city: '',
    postalCode: '',
    countryCode: 'ESP',
  })

  // Step 2: Bank Account
  const [bankData, setBankData] = useState({
    name: '',
    bankName: '',
    iban: '',
    swiftBic: '',
    accountNumber: '',
  })

  // Update email if user loads later
  useEffect(() => {
    if (user?.email && !profileData.email) {
      setProfileData(prev => ({ ...prev, email: user.email }))
    }
  }, [user?.email, profileData.email])

  const progressValue = (currentStep / TOTAL_STEPS) * 100

  const goToStep = useCallback((step: number) => {
    setDirection(step > currentStep ? 'forward' : 'backward')
    setIsAnimating(true)
    setTimeout(() => {
      setCurrentStep(step)
      setIsAnimating(false)
    }, 200)
  }, [currentStep])

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setProfileData(prev => ({ ...prev, [name]: value }))
  }

  const handleBankChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setBankData(prev => ({ ...prev, [name]: value }))
  }

  const handleStep1Submit = async () => {
    if (!profileData.companyName || !profileData.taxId) return

    try {
      await createSenderProfile.mutateAsync({
        name: profileData.companyName,
        companyName: profileData.companyName,
        taxId: profileData.taxId,
        email: profileData.email || undefined,
        phone: profileData.phone || undefined,
        street: profileData.street || undefined,
        city: profileData.city || undefined,
        postalCode: profileData.postalCode || undefined,
        countryCode: profileData.countryCode || undefined,
        isDefault: true,
      })
      goToStep(2)
    } catch {
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: t('settings.senderProfiles.createdDesc'),
      })
    }
  }

  const handleStep2Submit = async () => {
    if (!bankData.bankName || !bankData.iban) return

    try {
      await createBankAccount.mutateAsync({
        name: bankData.name || bankData.bankName,
        bankName: bankData.bankName,
        iban: bankData.iban || undefined,
        swiftBic: bankData.swiftBic || undefined,
        accountNumber: bankData.accountNumber || bankData.iban,
        isDefault: true,
      })
      goToStep(3)
    } catch {
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: t('settings.bankAccounts.createdDesc'),
      })
    }
  }

  const handleSkipBank = () => {
    goToStep(3)
  }

  const handleGoToDashboard = () => {
    navigate('/dashboard', { replace: true })
  }

  // Auto-redirect from welcome screen
  useEffect(() => {
    if (currentStep === 3) {
      const timer = setTimeout(() => {
        navigate('/dashboard', { replace: true })
      }, 6000)
      return () => clearTimeout(timer)
    }
  }, [currentStep, navigate])

  const stepIcons = [
    { icon: User, label: t('onboarding.personalInfo.title') },
    { icon: CreditCard, label: t('onboarding.bankAccount.title') },
    { icon: Check, label: t('onboarding.welcome.title') },
  ]

  return (
    <div className="min-h-screen bg-muted/40 flex flex-col">
      {/* Header */}
      <div className="w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold">GestorPlus</h1>
            <span className="text-sm text-muted-foreground">
              {t('onboarding.step', { current: currentStep, total: TOTAL_STEPS })}
            </span>
          </div>
          <Progress value={progressValue} className="h-2" />

          {/* Step indicators */}
          <div className="flex justify-between mt-3">
            {stepIcons.map((step, index) => {
              const StepIcon = step.icon
              const stepNumber = index + 1
              const isCompleted = currentStep > stepNumber
              const isCurrent = currentStep === stepNumber

              return (
                <div key={index} className="flex items-center gap-1.5">
                  <div
                    className={`
                      flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium
                      transition-all duration-300
                      ${isCompleted
                        ? 'bg-primary text-primary-foreground'
                        : isCurrent
                          ? 'bg-primary text-primary-foreground ring-2 ring-primary/30'
                          : 'bg-muted text-muted-foreground'
                      }
                    `}
                  >
                    {isCompleted ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : (
                      <StepIcon className="w-3.5 h-3.5" />
                    )}
                  </div>
                  <span className={`text-xs hidden sm:inline ${isCurrent ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                    {step.label}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-start justify-center px-4 py-8">
        <div
          className={`
            w-full max-w-2xl transition-all duration-200
            ${isAnimating
              ? direction === 'forward'
                ? 'opacity-0 translate-x-8'
                : 'opacity-0 -translate-x-8'
              : 'opacity-100 translate-x-0'
            }
          `}
        >
          {currentStep === 1 && (
            <Step1PersonalInfo
              data={profileData}
              onChange={handleProfileChange}
              onCountryChange={(value) => setProfileData(prev => ({ ...prev, countryCode: value }))}
              onSubmit={handleStep1Submit}
              isLoading={createSenderProfile.isPending}
            />
          )}

          {currentStep === 2 && (
            <Step2BankAccount
              data={bankData}
              onChange={handleBankChange}
              onSubmit={handleStep2Submit}
              onSkip={handleSkipBank}
              onBack={() => goToStep(1)}
              isLoading={createBankAccount.isPending}
            />
          )}

          {currentStep === 3 && (
            <Step3Welcome
              userName={user?.name || ''}
              onGoToDashboard={handleGoToDashboard}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Step 1: Personal/Business Info ──────────────────────────────

interface Step1Props {
  data: {
    companyName: string
    taxId: string
    email: string
    phone: string
    street: string
    city: string
    postalCode: string
    countryCode: string
  }
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onCountryChange: (value: string) => void
  onSubmit: () => void
  isLoading: boolean
}

function Step1PersonalInfo({ data, onChange, onCountryChange, onSubmit, isLoading }: Step1Props) {
  const { t } = useTranslation()

  const isValid = data.companyName.trim() !== '' && data.taxId.trim() !== ''

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isValid) onSubmit()
  }

  return (
    <Card>
      <CardHeader className="text-center">
        <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
          <Building2 className="w-6 h-6 text-primary" />
        </div>
        <CardTitle className="text-2xl">{t('onboarding.personalInfo.title')}</CardTitle>
        <CardDescription>{t('onboarding.personalInfo.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Company Name */}
          <div className="space-y-2">
            <Label htmlFor="companyName">{t('onboarding.personalInfo.companyName')} *</Label>
            <Input
              id="companyName"
              name="companyName"
              value={data.companyName}
              onChange={onChange}
              placeholder={t('onboarding.personalInfo.companyNamePlaceholder')}
              disabled={isLoading}
              autoFocus
            />
          </div>

          {/* Tax ID */}
          <div className="space-y-2">
            <Label htmlFor="taxId">{t('onboarding.personalInfo.taxId')} *</Label>
            <Input
              id="taxId"
              name="taxId"
              value={data.taxId}
              onChange={onChange}
              placeholder={t('onboarding.personalInfo.taxIdPlaceholder')}
              disabled={isLoading}
            />
          </div>

          {/* Email & Phone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('onboarding.personalInfo.email')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={data.email}
                onChange={onChange}
                placeholder={t('onboarding.personalInfo.emailPlaceholder')}
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">{t('onboarding.personalInfo.phone')}</Label>
              <PhoneInput
                id="phone"
                name="phone"
                value={data.phone}
                onChange={(val) => onChange({ target: { name: 'phone', value: val } } as React.ChangeEvent<HTMLInputElement>)}
                placeholder={t('onboarding.personalInfo.phonePlaceholder')}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label htmlFor="street">{t('onboarding.personalInfo.street')}</Label>
            <Input
              id="street"
              name="street"
              value={data.street}
              onChange={onChange}
              placeholder={t('onboarding.personalInfo.streetPlaceholder')}
              disabled={isLoading}
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">{t('onboarding.personalInfo.city')}</Label>
              <Input
                id="city"
                name="city"
                value={data.city}
                onChange={onChange}
                placeholder={t('onboarding.personalInfo.cityPlaceholder')}
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="postalCode">{t('onboarding.personalInfo.postalCode')}</Label>
              <Input
                id="postalCode"
                name="postalCode"
                value={data.postalCode}
                onChange={onChange}
                placeholder={t('onboarding.personalInfo.postalCodePlaceholder')}
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2 col-span-2 sm:col-span-1">
              <Label>{t('onboarding.personalInfo.country')}</Label>
              <CountrySelect
                value={data.countryCode}
                onChange={onCountryChange}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Submit */}
          <div className="pt-4">
            <Button type="submit" className="w-full" disabled={!isValid || isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.saving')}
                </>
              ) : (
                <>
                  {t('common.next')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

// ─── Step 2: Bank Account ────────────────────────────────────────

interface Step2Props {
  data: {
    name: string
    bankName: string
    iban: string
    swiftBic: string
    accountNumber: string
  }
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSubmit: () => void
  onSkip: () => void
  onBack: () => void
  isLoading: boolean
}

function Step2BankAccount({ data, onChange, onSubmit, onSkip, onBack, isLoading }: Step2Props) {
  const { t } = useTranslation()

  const isValid = data.bankName.trim() !== '' && data.iban.trim() !== ''

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isValid) onSubmit()
  }

  return (
    <Card>
      <CardHeader className="text-center">
        <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
          <CreditCard className="w-6 h-6 text-primary" />
        </div>
        <CardTitle className="text-2xl">{t('onboarding.bankAccount.title')}</CardTitle>
        <CardDescription>{t('onboarding.bankAccount.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Account Name */}
          <div className="space-y-2">
            <Label htmlFor="name">{t('onboarding.bankAccount.name')}</Label>
            <Input
              id="name"
              name="name"
              value={data.name}
              onChange={onChange}
              placeholder={t('onboarding.bankAccount.namePlaceholder')}
              disabled={isLoading}
              autoFocus
            />
          </div>

          {/* Bank Name */}
          <div className="space-y-2">
            <Label htmlFor="bankName">{t('onboarding.bankAccount.bankName')} *</Label>
            <Input
              id="bankName"
              name="bankName"
              value={data.bankName}
              onChange={onChange}
              placeholder={t('onboarding.bankAccount.bankNamePlaceholder')}
              disabled={isLoading}
            />
          </div>

          {/* IBAN */}
          <div className="space-y-2">
            <Label htmlFor="iban">{t('onboarding.bankAccount.iban')} *</Label>
            <Input
              id="iban"
              name="iban"
              value={data.iban}
              onChange={onChange}
              placeholder={t('onboarding.bankAccount.ibanPlaceholder')}
              disabled={isLoading}
            />
          </div>

          {/* SWIFT/BIC & Account Number */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="swiftBic">{t('onboarding.bankAccount.swiftBic')}</Label>
              <Input
                id="swiftBic"
                name="swiftBic"
                value={data.swiftBic}
                onChange={onChange}
                placeholder={t('onboarding.bankAccount.swiftBicPlaceholder')}
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="accountNumber">{t('onboarding.bankAccount.accountNumber')}</Label>
              <Input
                id="accountNumber"
                name="accountNumber"
                value={data.accountNumber}
                onChange={onChange}
                placeholder={t('onboarding.bankAccount.accountNumberPlaceholder')}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="pt-4 space-y-3">
            <Button type="submit" className="w-full" disabled={!isValid || isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.saving')}
                </>
              ) : (
                <>
                  {t('common.next')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>

            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onBack}
                disabled={isLoading}
              >
                <ArrowLeft className="mr-1 h-4 w-4" />
                {t('common.back')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onSkip}
                disabled={isLoading}
                className="text-muted-foreground"
              >
                {t('onboarding.bankAccount.skip')}
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

// ─── Step 3: Welcome Screen ──────────────────────────────────────

interface Step3Props {
  userName: string
  onGoToDashboard: () => void
}

function Step3Welcome({ userName, onGoToDashboard }: Step3Props) {
  const { t } = useTranslation()
  const [showContent, setShowContent] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 300)
    return () => clearTimeout(timer)
  }, [])

  const features = [
    { icon: FileText, text: t('onboarding.welcome.features.invoices') },
    { icon: Users, text: t('onboarding.welcome.features.clients') },
    { icon: BarChart3, text: t('onboarding.welcome.features.reports') },
  ]

  return (
    <div className="text-center space-y-8">
      {/* Animated icon */}
      <div className="animate-scale-in">
        <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
          <Sparkles className="w-10 h-10 text-primary" />
        </div>
      </div>

      {/* Title */}
      <div className="animate-fade-in-up" style={{ animationDelay: '0.2s', opacity: 0 }}>
        <h1 className="text-3xl font-bold tracking-tight">
          {t('onboarding.welcome.greeting', { name: userName })}
        </h1>
        <p className="text-muted-foreground mt-2 text-lg">
          {t('onboarding.welcome.subtitle')}
        </p>
      </div>

      {/* Features */}
      {showContent && (
        <div className="grid gap-4 max-w-md mx-auto">
          {features.map((feature, index) => {
            const FeatureIcon = feature.icon
            return (
              <div
                key={index}
                className="flex items-center gap-3 p-3 rounded-lg bg-background border animate-fade-in-up"
                style={{ animationDelay: `${0.4 + index * 0.15}s`, opacity: 0 }}
              >
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FeatureIcon className="w-5 h-5 text-primary" />
                </div>
                <span className="text-sm font-medium text-left">{feature.text}</span>
              </div>
            )
          })}
        </div>
      )}

      {/* CTA Button */}
      <div className="animate-fade-in-up" style={{ animationDelay: '0.9s', opacity: 0 }}>
        <Button size="lg" onClick={onGoToDashboard} className="px-8">
          {t('onboarding.welcome.goToDashboard')}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
